import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { V2TranslationService } from '@app/v2/services/translation.service'
import { WebhookEventOption } from '@app/v2/settings/components/webhooks/webhook-card/webhook-card.component'
import { WEBHOOK_TRIGGERS } from '@app/v2/settings/constants/webhook-triggers.constant'
import { CreateWebhook, UpdateWebhookPayload, Webhook } from '@app/v2/settings/enums/webhooks.model'
import { WebhookIcons } from '@app/v2/settings/models/organization-settings.model'
import { webhookHttpErrorMessage, WebhookService } from '@app/v2/settings/services/webhook.service'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { finalize } from 'rxjs'

@Component({
  selector: 'create-webhook-modal',
  standalone: true,
  imports: [ModalComponent, BasicInputComponent, SelectInputComponent, ReactiveFormsModule, TranslateModule, NgxTippyModule, ButtonSpinnerDirective],
  templateUrl: './create-webhook-modal.component.html'
})
export class CreateWebhookModalComponent implements OnInit {
  @Input() id: string = 'create-webhook-modal'
  @Input() events: WebhookEventOption[] = []
  @Input() isLoadingEvents = false
  @Input() webhook: Webhook | null = null
  @Output() webhookCreated = new EventEmitter<void>()
  @Output() webhookUpdated = new EventEmitter<Webhook>()
  @Output() cancelled = new EventEmitter<void>()

  public readonly BasicFieldType = BasicFieldType
  public readonly webhookTriggers = WEBHOOK_TRIGGERS
  public readonly isSubmitting = signal(false)
  public readonly plusIcon = WebhookIcons.getIcon().plusIcon
  public readonly editIcon = WebhookIcons.getIcon().editIcon
  public form = new FormGroup({
    event_ids: new FormControl<number[]>([], [this._minArrayLength(1)]),
    event_id: new FormControl<number | null>(null),
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    webhook_url: new FormControl('', { nonNullable: true, validators: [Validators.required, this._urlValidator] }),
    triggers: new FormControl<string[]>([], [this._minArrayLength(1)]),
    signing_secret_encrypted: new FormControl('', { nonNullable: true })
  })

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _authService = inject(AuthService)
  private readonly _webhookService = inject(WebhookService)
  private readonly _translationService = inject(V2TranslationService)
  private readonly _translate = inject(TranslateService)
  private readonly _alertService = inject(AlertService)
  private _hasSigningSecretChanged = false

  public readonly securityStringTooltip = this._translate.instant('v2.orgSettings.webhooks.securityStringTooltip')
  public readonly securityStringTippyProps = {
    theme: 'dark' as const,
    maxWidth: 317,
    placement: (this._translationService.isRTL() ? 'top-start' : 'top-end') as 'top-start' | 'top-end',
    appendTo: () => document.body,
    zIndex: 200_000
  }

  ngOnInit(): void {
    if (this.webhook) this._prefillForm(this.webhook)
    else this._resetForm()

    this.form.controls.signing_secret_encrypted.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (!this.isEditMode()) return
      this._hasSigningSecretChanged = true
    })
  }

  /**
   * Whether the modal is editing an existing webhook.
   * @returns {boolean}
   */
  public isEditMode(): boolean {
    return this.webhook != null
  }

  /**
   * Event options for the picker, including the webhook's current event when it is missing from the loaded list.
   * @returns {WebhookEventOption[]}
   */
  public eventOptions(): WebhookEventOption[] {
    const webhook = this.webhook
    if (!webhook) return this.events

    const eventId = Number(webhook.event_id)
    if (this.events.some((event) => event.id === eventId)) return this.events

    return [{ id: eventId, name: webhook.event_slug, slug: webhook.event_slug }, ...this.events]
  }

  /**
   * Closes the modal without saving.
   */
  public handleModalClose(): void {
    if (this.isSubmitting()) return
    this._resetForm()
    this.cancelled.emit()
  }

  /**
   * Saves a new webhook or updates the webhook being edited.
   */
  public onSave(): void {
    if (this.isSubmitting()) return

    this.form.markAllAsTouched()
    if (this.form.invalid) return

    if (this.isEditMode()) {
      this._updateWebhook()
      return
    }

    this._createWebhook()
  }

  /**
   * Whether the save action should be disabled.
   * @returns {boolean}
   */
  public isSaveDisabled(): boolean {
    return this.isSubmitting() || this.isLoadingEvents || this.form.invalid
  }

  /**
   * Creates a webhook from the form.
   */
  private _createWebhook(): void {
    const organizationId = this._authService.currentWorkspace?.id
    const eventIds = this.form.controls.event_ids.value ?? []
    const eventSlugs = this.events.filter((event) => event.id != null && eventIds.includes(event.id)).map((event) => event.slug)

    const payload: CreateWebhook = {
      organization_id: organizationId,
      event_ids: eventIds,
      event_slugs: eventSlugs,
      name: this.form.controls.name.value.trim(),
      webhook_url: this.form.controls.webhook_url.value.trim(),
      triggers: this.form.controls.triggers.value ?? [],
      active: true
    }

    const signingSecret = this.form.controls.signing_secret_encrypted.value.trim()
    if (signingSecret) payload.signing_secret_encrypted = signingSecret

    this.isSubmitting.set(true)
    this._webhookService
      .createWebhook(payload)
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._resetForm()
          this.webhookCreated.emit()
          this._alertService.alert(Alert.SUCCESS, `${payload.name} ${this._translate.instant('v2.orgSettings.webhooks.createWebhookSuccess')}`)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, webhookHttpErrorMessage(error, this._translate.instant('v2.orgSettings.webhooks.createWebhookError')))
        }
      })
  }

  /**
   * Updates the webhook currently being edited.
   */
  private _updateWebhook(): void {
    const webhook = this.webhook
    if (!webhook) return

    const eventId = this.form.controls.event_id.value
    if (eventId == null) return

    const eventSlug = this.events.find((event) => event.id === eventId)?.slug ?? webhook.event_slug
    const payload: UpdateWebhookPayload = {
      event_id: eventId,
      event_slug: eventSlug,
      name: this.form.controls.name.value.trim(),
      webhook_url: this.form.controls.webhook_url.value.trim(),
      triggers: this.form.controls.triggers.value ?? [],
      active: webhook.active
    }

    const signingSecret = this.form.controls.signing_secret_encrypted.value.trim()
    if (this._hasSigningSecretChanged && signingSecret) {
      payload.signing_secret_encrypted = signingSecret
    }

    this.isSubmitting.set(true)
    this._webhookService
      .updateWebhook(webhook.id, payload)
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (updatedWebhook) => {
          this._resetForm()
          this.webhookUpdated.emit(updatedWebhook)
          this._alertService.alert(Alert.SUCCESS, `${updatedWebhook.name} ${this._translate.instant('v2.orgSettings.webhooks.updateWebhookSuccess')}`)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, webhookHttpErrorMessage(error, this._translate.instant('v2.orgSettings.webhooks.updateWebhookError')))
        }
      })
  }

  /**
   * Prefills the form from an existing webhook.
   * @param webhook {Webhook}
   */
  private _prefillForm(webhook: Webhook): void {
    this._hasSigningSecretChanged = false
    this.form.controls.event_ids.clearValidators()
    this.form.controls.event_id.setValidators([Validators.required])
    this.form.reset(
      {
        event_ids: [],
        event_id: Number(webhook.event_id),
        name: webhook.name,
        webhook_url: webhook.webhook_url,
        triggers: webhook.triggers ?? [],
        signing_secret_encrypted: webhook.signing_secret_encrypted ?? ''
      },
      { emitEvent: false }
    )
    this.form.controls.event_ids.updateValueAndValidity({ emitEvent: false })
    this.form.controls.event_id.updateValueAndValidity({ emitEvent: false })
    this.form.enable({ emitEvent: false })
    this.form.markAsUntouched()
  }

  /**
   * Clears the create/edit form.
   */
  private _resetForm(): void {
    this._hasSigningSecretChanged = false
    this.form.controls.event_id.clearValidators()
    this.form.controls.event_ids.setValidators([this._minArrayLength(1)])
    this.form.reset({
      event_ids: [],
      event_id: null,
      name: '',
      webhook_url: '',
      triggers: [],
      signing_secret_encrypted: ''
    })
    this.form.controls.event_id.updateValueAndValidity({ emitEvent: false })
    this.form.controls.event_ids.updateValueAndValidity({ emitEvent: false })
    this.form.enable({ emitEvent: false })
    this.form.markAsUntouched()
  }

  /**
   * Requires the control value to be an array with at least `min` items.
   * @param min {number}
   */
  private _minArrayLength(min: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!Array.isArray(value) || value.length < min) return { minSelections: true }
      return null
    }
  }

  /**
   * Validates that the control contains an http(s) URL when a value is present.
   * @param control {AbstractControl}
   * @returns {ValidationErrors | null}
   */
  private _urlValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value as string)?.trim() ?? ''
    if (!value) return null
    try {
      const url = new URL(value)
      if (url.protocol === 'http:' || url.protocol === 'https:') return null
    } catch {
      return { invalidUrl: true }
    }
    return { invalidUrl: true }
  }
}
